import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useToast } from '../../utils/toastUtils';
import ToastContainer from '../common/ToastContainer';
import ButtonList from '../common/ButtonList';
import CounterSummary from '../common/CounterSummary';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorAlert from '../common/ErrorAlert';
import ButtonFormModal from '../common/ButtonFormModal';
import { useWebSocketCounter } from '../../hooks/useWebSocketCounter';
import { useClickBatcher } from '../../hooks/useClickBatcher';
import { useButtonForm } from '../../hooks/useButtonForm';
import { useUser } from '../../contexts/UserContext';
import { useOwnerUrl } from '../../hooks/useOwnerUrl';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useCounters } from '../../contexts/CounterContext';
import { updateCounterMetaTags, resetMetaTags } from '../../utils/metaTagsUtils';
import { config } from '../../config';
import { formatDateWithTimezone, formatCountdown } from '../../utils/dateUtils';
import CreateCounterModal from '../modals/CreateCounterModal';
import { Modal } from 'bootstrap';
import { isOwnerUrlIdentifier, findCounterByIdentifier } from '../../utils/urlUtils';

/**
 * Counter component displays a specific counter and its buttons.
 * 
 * This component is responsible for:
 * - Displaying the counter's custom buttons for tapping
 * - Allowing button creation/editing for admin users or owner URL access
 * - Handling button taps to increment counts
 * 
 * The component uses various hooks to manage counter data, button interactions,
 * and user permissions.
 */
function Counter() {
  const { t } = useTranslation();
  const { toasts, showToast } = useToast();
  const { counterId } = useParams<{ counterId?: string }>();
  const { user, isAdmin, hasCounterAccess } = useUser();
  const userEmail = user.email;
  const { counters, refreshCounters, addCounter, loading: countersLoading } = useCounters();
  const navigate = useNavigate();
  const [counterNotFound, setCounterNotFound] = useState(false);
  const [isCreatingCounter, setIsCreatingCounter] = useState(false);
  const createModalRef = useRef<Modal | null>(null);
  
  // Use the new owner URL hook
  const { isOwnerUrl, counterIdentifier } = useOwnerUrl(counterId);
  
  // Use the WebSocket counter hook for real-time data updates
  const { 
    buttons, 
    loading, 
    error,
    lastUpdate, 
    refreshData
  } = useWebSocketCounter(counterIdentifier || '', isOwnerUrl);
  
  // Setup the click batching hook for handling button clicks
  const {
    registerClick,
    hasPendingClicks,
    getPendingClickCount,
    hasFailedUpdates,
    getPendingButtonIds,
    forceSendBatch,
    setUpdateCallback,
    isProcessing
  } = useClickBatcher(counterIdentifier || '', isOwnerUrl);
  
  // State for initial load tracking
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Use the button form hook
  const {
    formData,
    isEditMode,
    handleFormChange,
    handleAddNew,
    handleSubmit,
    popularIcons,
    popularColors,
    isLoggedIn
  } = useButtonForm({
    counterId: counterIdentifier || '',
    onSuccess: refreshData,
    isOwnerUrl
  });
  
  // Add a timeUpdateTrigger state to force UI updates for the countdown
  const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0);
  const [wasExpired, setWasExpired] = useState(false);
  
  // Add refs to track previous counter settings
  const prevCountdownMinutesRef = useRef<number | undefined>(undefined);
  const prevCountdownStartedAtRef = useRef<string | null>(null);
  
  // Function to open the create counter modal
  const openCreateModal = () => {
    const modalEl = document.getElementById('createCounterModal');
    if (modalEl) {
      const modal = new Modal(modalEl);
      createModalRef.current = modal;
      modal.show();
    }
  };

  // Function to handle counter creation
  const handleCreateCounter = async (name: string, isPrivate?: boolean, email?: string) => {
    try {
      setIsCreatingCounter(true);
      const newCounter = await addCounter(name, isPrivate, email);
      
      // Hide the modal
      if (createModalRef.current) {
        createModalRef.current.hide();
      }
      
      // Navigate to the new counter
      navigate(`/${newCounter.ownerUrl}`);
    } catch (err) {
      showToast(t('errors.failedToCreate', 'Failed to create counter'), 'danger');
    } finally {
      setIsCreatingCounter(false);
    }
  };
  
  // Get the counter name to use in the document title
  const currentCounter = counterIdentifier ? 
    counters.find(c => isOwnerUrl ? c.ownerUrl === counterIdentifier : c._id === counterIdentifier) : 
    undefined;
    
  // Get the total pending clicks across all buttons
  const pendingClicksCount = currentCounter?.buttons ? currentCounter.buttons.reduce((sum, button) => 
    sum + (hasPendingClicks(button._id) ? getPendingClickCount(button._id) : 0), 0) : 0;
    
  // Calculate total pending clicks for the summary
  const totalPendingClicks: number = pendingClicksCount || 0;
  
  // Get counter details directly to ensure we have the latest data
  const counterDetails = counterIdentifier ? findCounterByIdentifier(counterIdentifier, counters) : undefined;
  
  // Determine if this is an owner URL using our consistent utility
  const isOwnerUrlAccess = counterIdentifier ? isOwnerUrlIdentifier(counterIdentifier, counters) : false;
  
  // Use our centralized access control with direct counter reference
  const hasViewAccess = counterDetails ? hasCounterAccess(counterDetails.ownerEmail || null, isOwnerUrlAccess, 'view') : false;
  const hasManageAccess = counterDetails ? hasCounterAccess(counterDetails.ownerEmail || null, isOwnerUrlAccess, 'manage') : false;

  // Determine admin controls
  const isOwner = currentCounter ? currentCounter.ownerEmail === userEmail : false;
  const showAdminControls = hasManageAccess;
  
  // Also need to update the isCounterExpired function to use useMemo and timeUpdateTrigger
  const isCounterExpired = React.useMemo((): boolean => {
    // Use server-provided isExpired flag if available
    if (currentCounter?.isExpired !== undefined) {
      return currentCounter.isExpired;
    }
    
    // Fallback to client-side calculation
    if (!currentCounter || !currentCounter.timeLimitType) return false;
    
    const now = new Date();
    
    if (currentCounter.timeLimitType === 'deadline' && currentCounter.deadline) {
      const deadlineDate = new Date(currentCounter.deadline);
      
      // Use timestamp comparison for timezone-independent check
      return now.getTime() > deadlineDate.getTime();
    }
    
    if (currentCounter.timeLimitType === 'countdown' && 
        currentCounter.countdownStartedAt && 
        currentCounter.countdownMinutes) {
      const startedAt = new Date(currentCounter.countdownStartedAt);
      // Convert countdownMinutes to milliseconds correctly
      const countdownMs = currentCounter.countdownMinutes * 60 * 1000;
      const endTimeMs = startedAt.getTime() + countdownMs;
      return now.getTime() > endTimeMs;
    }
    
    return false;
  }, [currentCounter?.isExpired, currentCounter?.timeLimitType, 
      currentCounter?.deadline, currentCounter?.countdownStartedAt, 
      currentCounter?.countdownMinutes, timeUpdateTrigger]);
  
  // Set up the optimistic UI update for clicks
  useEffect(() => {
    // This callback will handle UI updates directly without relying on WebSocket
    setUpdateCallback((buttonId) => {
      // UI updates for clicks are handled directly by the click batcher's 
      // pending count mechanism rather than modifying the actual button data
    });
  }, [setUpdateCallback]);
  
  // Add monitoring for button count changes to help diagnose the multiplication bug
  useEffect(() => {
    if (!initialLoadComplete) return;
    
    // Create a snapshot of button counts for comparison
    const buttonCounts = buttons.reduce((acc, button) => {
      acc[button._id] = button.count;
      return acc;
    }, {} as Record<string, number>);
    
    // This effect runs whenever the buttons array changes
    // We can use it to monitor for unexpected multiplication of counts
  }, [buttons, initialLoadComplete]);
  
  // Update meta tags for social sharing when counter data changes
  useEffect(() => {
    if (currentCounter) {
      // Update meta tags with dynamic counter information
      updateCounterMetaTags(currentCounter, buttons);
    }
    
    // Reset meta tags when component unmounts
    return () => {
      resetMetaTags();
    };
  }, [currentCounter, buttons]);

  useEffect(() => {
    // Set initial load complete when buttons are loaded
    if (!initialLoadComplete && !loading && buttons.length >= 0) {
      setInitialLoadComplete(true);
    }
  }, [loading, buttons, initialLoadComplete]);
  
  // Update counter context when WebSocket data changes
  useEffect(() => {
    // When lastUpdate changes via WebSocket updates, we should update our counter data
    if (lastUpdate) {
      // Reset the expired state if needed
      if (wasExpired && currentCounter && !isCounterExpired) {
        setWasExpired(false);
      }
    }
  }, [lastUpdate, wasExpired, currentCounter, isCounterExpired, refreshCounters]);

  // Monitor for changes to the current counter settings
  useEffect(() => {
    // Track changes in countdown settings
    if (currentCounter && currentCounter.timeLimitType === 'countdown') {
      // If countdownMinutes or countdownStartedAt has changed, update the timer
      if (prevCountdownMinutesRef.current !== currentCounter.countdownMinutes ||
          prevCountdownStartedAtRef.current !== currentCounter.countdownStartedAt) {
        // Force an immediate timer update
        setTimeUpdateTrigger(prev => prev + 1);
      }
      
      // Update the refs for the next comparison
      prevCountdownMinutesRef.current = currentCounter.countdownMinutes;
      prevCountdownStartedAtRef.current = currentCounter.countdownStartedAt || null;
    }
  }, [currentCounter?.timeLimitType, currentCounter?.countdownMinutes, currentCounter?.countdownStartedAt]);
  
  // Get remaining time for display using useMemo to ensure it updates with the timer
  const remainingTimeText = React.useMemo((): string => {
    if (!currentCounter || !currentCounter.timeLimitType) return '';
    
    const now = new Date();
    
    if (currentCounter.timeLimitType === 'deadline' && currentCounter.deadline) {
      // Create a date from the deadline string
      const deadlineDate = new Date(currentCounter.deadline);
      
      // Get the user's timezone from the counter or fall back to browser timezone
      const userTimezone = currentCounter.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Check if deadline has passed
      if (now > deadlineDate) return t('counters.expired', 'Expired');
      
      // Calculate time difference in milliseconds - this is timezone-independent
      const diff = Math.max(0, deadlineDate.getTime() - now.getTime());
      
      // Calculate all time units more accurately
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      // For longer time periods, use a more accurate calculation
      const totalDays = Math.floor(diff / (1000 * 60 * 60 * 24));
      const years = Math.floor(totalDays / 365);
      const months = Math.floor((totalDays % 365) / 30);
      const days = Math.floor(totalDays % 30);
      
      // Generate time components without zeros
      const timeComponents: [number, string][] = [
        [years, t('time.years', 'years')],
        [months, t('time.months', 'months')],
        [days, t('time.days', 'days')],
        [hours, t('time.hours', 'hours')],
        [minutes, t('time.minutes', 'minutes')],
        [seconds, t('time.seconds', 'seconds')]
      ];
      
      // Filter out zero values for a cleaner display
      const significantComponents = timeComponents.filter(([value]) => value > 0);
      
      // Always show at least one component (even if all are zero)
      if (significantComponents.length === 0) {
        return `0 ${t('time.seconds', 'seconds')}`;
      }
      
      // Build the descriptive time remaining string
      return significantComponents
        .map(([value, unit]) => `${value} ${value === 1 ? unit.replace(/s$/, '') : unit}`)
        .join(', ');
    }
    
    if (currentCounter.timeLimitType === 'countdown' && 
        currentCounter.countdownStartedAt && 
        currentCounter.countdownMinutes) {
      const startedAt = new Date(currentCounter.countdownStartedAt);
      // Convert countdownMinutes to milliseconds correctly
      const countdownMs = currentCounter.countdownMinutes * 60 * 1000;
      const endTime = new Date(startedAt.getTime() + countdownMs);
      
      if (now > endTime) return t('counters.expired', 'Expired');
      
      const diff = Math.max(0, endTime.getTime() - now.getTime());
      
      // Calculate all time units more accurately
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      
      // For longer time periods, use a more accurate calculation
      const totalDays = Math.floor(diff / (1000 * 60 * 60 * 24));
      const years = Math.floor(totalDays / 365);
      const months = Math.floor((totalDays % 365) / 30);
      const days = Math.floor(totalDays % 30);
      
      // Generate time components without zeros
      const timeComponents: [number, string][] = [
        [years, t('time.years', 'years')],
        [months, t('time.months', 'months')],
        [days, t('time.days', 'days')],
        [hours, t('time.hours', 'hours')],
        [minutes, t('time.minutes', 'minutes')],
        [seconds, t('time.seconds', 'seconds')]
      ];
      
      // Filter out zero values for a cleaner display
      const significantComponents = timeComponents.filter(([value]) => value > 0);
      
      // Always show at least one component (even if all are zero)
      if (significantComponents.length === 0) {
        return `0 ${t('time.seconds', 'seconds')}`;
      }
      
      // Build the descriptive time remaining string
      return significantComponents
        .map(([value, unit]) => `${value} ${value === 1 ? unit.replace(/s$/, '') : unit}`)
        .join(', ');
    }
    
    return '';
  }, [currentCounter?.timeLimitType, currentCounter?.deadline, 
      currentCounter?.countdownStartedAt, currentCounter?.countdownMinutes, 
      t, timeUpdateTrigger]);
  
  // Add a timer effect for updating the countdown display
  useEffect(() => {
    // Only set up the timer if we have a counter with a time limit
    if (currentCounter?.timeLimitType) {
      // Create a second-by-second timer for updating the time display
      const timer = setInterval(() => {
        // Check if the counter is expired
        const now = new Date();
        let isCurrentlyExpired = false;
        
        if (currentCounter.timeLimitType === 'deadline' && currentCounter.deadline) {
          const deadlineDate = new Date(currentCounter.deadline);
          // Use timestamp comparison for timezone-independent check
          isCurrentlyExpired = now.getTime() > deadlineDate.getTime();
        } else if (currentCounter.timeLimitType === 'countdown' && 
                   currentCounter.countdownStartedAt && 
                   currentCounter.countdownMinutes) {
          const startedAt = new Date(currentCounter.countdownStartedAt);
          // Convert countdownMinutes to milliseconds correctly
          const countdownMs = currentCounter.countdownMinutes * 60 * 1000;
          const endTimeMs = startedAt.getTime() + countdownMs;
          isCurrentlyExpired = now.getTime() > endTimeMs;
        }
        
        // If the counter just expired (transition from not expired to expired)
        // Refresh the data to update the server state
        if (isCurrentlyExpired && !wasExpired) {
          setWasExpired(true);
          refreshCounters();
        }
        
        // Update trigger to force recalculation of time-related values
        setTimeUpdateTrigger(prev => prev + 1);
      }, 1000); // Update every second
      
      // Clean up the timer when component unmounts or dependencies change
      return () => clearInterval(timer);
    }
  }, [currentCounter?.timeLimitType, currentCounter?.deadline, 
      currentCounter?.countdownStartedAt, currentCounter?.countdownMinutes,
      wasExpired, refreshCounters]);
  
  // If no identifier is provided, redirect to counters list
  if (!counterIdentifier) {
    return <Navigate to="/counters" replace />;
  }
  
  // Don't make any redirect decisions while counters are still loading
  if (countersLoading) {
    return <LoadingSpinner size="lg" />;
  }
  
  // If counter not found, redirect to counter not found page
  if (counterNotFound) {
    return <Navigate to="/counter-not-found" replace />;
  }

  // Check if we already have the counter from our counter hook
  if (!currentCounter) {
    // Try to find the counter
    const foundCounter = findCounterByIdentifier(counterIdentifier, counters);
    
    // If counter not found after trying to get it, go to not found page
    if (!foundCounter && !countersLoading) {
      return <Navigate to="/counter-not-found" replace />;
    }
  }
  
  // Double-check we have counter details before checking access
  // Only redirect if we're sure counters have loaded
  if (!counterDetails && !countersLoading) {
    return <Navigate to="/counter-not-found" replace />;
  }
  
  // Don't attempt access control checks if we don't have counter details yet
  if (!counterDetails) {
    return <LoadingSpinner size="lg" />;
  }
  
  // Redirect if no view access
  if (!hasViewAccess) {
    // For owner URLs of logged-in user counters, show "not found" page if access is denied for public users
    if (isOwnerUrlAccess && counterDetails.ownerEmail !== null && user.email === null) {
      return <Navigate to="/counter-not-found" replace />;
    }
    
    // For other denied access cases, redirect to counters list
    return <Navigate to="/counters" replace />;
  }

  /**
   * Handle button click and register with batch processor
   */  
  const handleButtonClick = (buttonId: string) => {
    // Early validation
    if (!buttonId || !currentCounter || isCounterExpired) {
      return;
    }
    
    // Find the button to get its cost
    const button = buttons.find(b => b._id === buttonId);
    const buttonCost = button?.costToClick || 0;
    
    try {
      // Register the click with the batcher, including the button cost
      // for token balance tracking
      registerClick(buttonId, buttonCost);
    } catch (error) {
      showToast(
        t('counters.clickError', 'Failed to register click'),
        'danger'
      );
    }
  };

  // Force send any pending clicks immediately
  const handleForceSendBatch = () => {
    if (pendingClicksCount > 0) {
      forceSendBatch();
    }
  };

  // Render a loading spinner while data is being fetched
  if (loading && !initialLoadComplete) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <LoadingSpinner />
      </div>
    );
  }

  // Display error banner if something went wrong
  if (error) {
    return <ErrorAlert message={error} />;
  }

  // Get total count across all buttons
  const totalButtonClicks = buttons.reduce((sum, button) => sum + button.count, 0);
  
  // Render counter deadline info if applicable
  const renderDeadlineInfo = () => {
    if (!currentCounter) return null;
    
    return (
      <div className="text-center mb-4">
        {currentCounter.deadline && (
          <div className="deadline-info">
            <p className="mb-1">
              <i className="bi bi-calendar-event me-2"></i>
              {formatDateWithTimezone(
                currentCounter.deadline,
                currentCounter.timezone || 'UTC'
              )}
            </p>
            {currentCounter.countdownMinutes !== undefined && (
              <p className="countdown mb-0">
                <i className="bi bi-alarm me-2"></i>
                {formatCountdown(currentCounter.countdownMinutes)}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container py-3 py-md-5">
      <div className="row justify-content-center">
        <div className="col-lg-10">
          {/* Header action button */}
          {showAdminControls && (
            <div className="d-flex justify-content-center mb-3">
              <button className="btn btn-primary" onClick={handleAddNew}>
                <i className="bi bi-plus-lg me-2"></i>
                {t('counters.addNewButton', 'Add New Button')}
              </button>
            </div>
          )}

          {/* Counter time limit information */}
          {currentCounter?.timeLimitType && (
            <div className="alert alert-info mb-4">
              <h5 className="mb-0">
                {currentCounter.timeLimitType === 'deadline' ? (
                  <>
                    {isCounterExpired ? (
                      <span className="text-danger">
                        <i className="bi bi-clock-history me-1"></i>
                        {t('counters.expiredOn', 'Expired on')} {currentCounter.deadline && formatDateWithTimezone(currentCounter.deadline)}
                      </span>
                    ) : (
                      <>
                        <i className="bi bi-calendar-event me-1"></i>
                        {t('counters.deadline', 'Deadline')}: {currentCounter.deadline && formatDateWithTimezone(currentCounter.deadline)}
                        <div className="mt-1 small text-primary">
                          <i className="bi bi-hourglass-split me-1"></i>
                          {remainingTimeText}
                        </div>
                      </>
                    )}
                  </>
                ) : currentCounter.timeLimitType === 'countdown' ? (
                  <>
                    {isCounterExpired ? (
                      <span className="text-danger">
                        <i className="bi bi-clock-history me-1"></i>
                        {t('counters.expired', 'Expired')}
                      </span>
                    ) : (
                      <>
                        <i className="bi bi-countdown me-1"></i>
                        {t('counters.countdownRunning', 'Countdown running')}: {currentCounter.countdownMinutes && formatCountdown(currentCounter.countdownMinutes * 60)}
                        <div className="mt-1 small text-primary">
                          <i className="bi bi-hourglass-split me-1"></i>
                          {remainingTimeText}
                        </div>
                      </>
                    )}
                  </>
                ) : null}
              </h5>
            </div>
          )}

          {/* Counter summary statistics - only show when there are buttons */}
          {buttons.length > 0 && (
            <div className="mb-4 py-4 px-3 bg-body-tertiary rounded">
              <div className="text-center">
                <h5 className="fs-6 text-muted mb-2">
                  {t('counters.totalTaps', 'Total Taps')}
                </h5>
                <h2 className="display-6 fw-bold text-primary mb-0">
                  {totalButtonClicks}
                  {totalPendingClicks > 0 && (
                    <small className="text-muted ms-2">
                      +{totalPendingClicks} {t('counters.pending', 'pending')}
                    </small>
                  )}
                </h2>
              </div>
              {totalPendingClicks > 0 && (
                <div className="mt-3 text-center">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleForceSendBatch}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i>
                    {t('counters.syncNow', 'Sync Now')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Display buttons with click handlers */}
          <div className="mb-4">
            <ButtonList 
              buttons={buttons}
              onButtonClick={handleButtonClick}
              disabled={isCounterExpired}
              onEdit={showAdminControls && buttons.length > 0 ? handleAddNew : undefined}
              hasPendingClicks={hasPendingClicks}
              getPendingClickCount={getPendingClickCount}
              hasFailedUpdates={hasFailedUpdates}
              isOwnerUrl={isOwnerUrl}
              openCreateModal={openCreateModal}
            />
          </div>

          {/* Button form modal for adding/editing buttons */}
          <ButtonFormModal 
            modalId="buttonModal"
            formData={formData}
            isEditMode={isEditMode}
            popularColors={popularColors}
            popularIcons={popularIcons}
            onFormChange={handleFormChange}
            onSubmit={handleSubmit}
            isLoggedIn={isLoggedIn}
          />

          {/* Create Counter Modal */}
          <CreateCounterModal 
            isCreating={isCreatingCounter} 
            onCreateCounter={handleCreateCounter}
          />
        </div>
      </div>

      {/* Toast container for displaying notifications */}
      <ToastContainer toasts={toasts} />
    </div>
  );
}

export default Counter; 